import { z } from 'zod';

import { MessageIdSchema } from './message_envelope';
import { MessageFeedbackReasonSchema } from './message_feedback';

/**
 * Nota da SESSÃO (conversa inteira) dada pelo viajante no web-chat — o
 * "How is Claude doing this session?" do VOLL (spec session-rating 2026-09-29).
 * UI: Ruim · Ok · Boa. Diferente do 👍/👎 (por mensagem) e do NPS (0–10, bancada).
 */
export const SessionRatingValueSchema = z.enum(['bad', 'fine', 'good']);
export type SessionRatingValue = z.infer<typeof SessionRatingValueSchema>;

/** Valor NUMERIC do score `session_rating` no Langfuse (média pronta no dashboard). */
export const SESSION_RATING_SCORE_VALUE: Readonly<Record<SessionRatingValue, number>> = {
  bad: 1,
  fine: 2,
  good: 3,
};

/**
 * Corpo do POST da resposta (web-chat → BFF → conversation). `dismissed` = ✕.
 * Motivos/comentário só com `bad` (os mesmos 6 do 👎). `comment` é PII: fica no
 * Mongo, vai mascarado ao Langfuse, nunca volta ao cliente. Nunca compor este
 * schema com `.and(...)`: a interseção do zod descarta o `.refine`.
 */
export const SessionRatingAnswerRequestSchema = z
  .object({
    value: z.union([SessionRatingValueSchema, z.literal('dismissed')]),
    reasons: z.array(MessageFeedbackReasonSchema).max(6).optional(),
    comment: z.string().trim().min(1).max(500).optional(),
  })
  .strict()
  .refine((b) => b.value === 'bad' || (b.reasons === undefined && b.comment === undefined), {
    message: 'reasons/comment only with value "bad"',
  });
export type SessionRatingAnswerRequest = z.infer<typeof SessionRatingAnswerRequestSchema>;

/**
 * O que o cliente vê. `undecided` = sem decisão gravada (só ele autoriza o
 * decide); `none` = decidido que não (fora do sorteio, operador, teto, feature
 * desligada — o cliente não precisa do motivo).
 */
export const SessionRatingStatusSchema = z.enum([
  'undecided',
  'none',
  'invited',
  'answered',
  'dismissed',
]);
export const SessionRatingStateSchema = z
  .object({
    status: SessionRatingStatusSchema,
    afterMessageId: MessageIdSchema.optional(),
    value: SessionRatingValueSchema.optional(),
  })
  .strict();
export type SessionRatingState = z.infer<typeof SessionRatingStateSchema>;
